import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    TextField,
    Button,
    Card,
    CardContent,
    Typography,
    Alert,
    CircularProgress,
    Chip,
    List,
    ListItem,
    IconButton,
    Collapse,
    Tooltip
} from '@mui/material';
import {
    Search as SearchIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    WifiOff as NetworkIcon,
    Security as SecurityIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import MarkingService from '../services/MarkingService';

const ChestnyZnakCodeChecker = () => {
    const [codeInput, setCodeInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [checkingHistory, setCheckingHistory] = useState([]);
    const [apiStatus, setApiStatus] = useState(null);
    const [error, setError] = useState('');
    const [expandedError, setExpandedError] = useState(null); // ID развёрнутой ошибки в истории
    const [lastResult, setLastResult] = useState(null); // Последний результат для визуальной индикации
    const inputRef = useRef(null); // Ref для автофокуса поля ввода

    const checkAPIStatus = async () => {
        try {
            const status = await MarkingService.getChestnyZnakStatus();
            setApiStatus(status);
        } catch (error) {
            console.error('Ошибка проверки статуса API:', error);
            setApiStatus({ status: 'error', message: error.message });
        }
    };

    const handleCheckCode = useCallback(async () => {
        const code = codeInput.trim();
        if (!code) {
            setError('Введите код маркировки');
            return;
        }

        setIsLoading(true);
        setError('');
        setLastResult(null); // Сбрасываем предыдущий результат

        try {
            const result = await MarkingService.quickCodeCheck(code);

            // Сохраняем последний результат для визуальной индикации
            setLastResult(result);

            // Добавляем результат в историю
            const historyItem = {
                id: Date.now(),
                code: code,
                timestamp: new Date(), // Всегда объект Date
                ...result
            };

            // Если в результате есть timestamp строка, оставляем как есть для детальной информации,
            // но для отображения в истории используем наш timestamp
            if (result.timestamp && typeof result.timestamp === 'string') {
                historyItem.serverTimestamp = result.timestamp;
            }

            setCheckingHistory(prev => [historyItem, ...prev.slice(0, 9)]); // Храним последние 10 проверок
            setCodeInput('');

            // Сразу устанавливаем фокус обратно на поле ввода для следующего сканирования
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);

        } catch (error) {
            console.error('Ошибка проверки кода:', error);
            setError(error.message || 'Произошла ошибка при проверке кода');

            // Сохраняем ошибку как последний результат для индикации
            setLastResult({
                source: 'error',
                valid: false,
                message: error.message || 'Произошла ошибка'
            });

            // Все равно возвращаем фокус на поле ввода
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        } finally {
            setIsLoading(false);
        }
    }, [codeInput]);

    // Загрузка статуса API и автофокус при монтировании компонента
    useEffect(() => {
        checkAPIStatus();
        // Устанавливаем фокус на поле ввода при монтировании
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    }, []);

    // Обработка сканирования штрихкода
    useEffect(() => {
        async function handleKeyDown(e) {
            if (e.key === 'Enter' && codeInput.trim().length > 0) {
                e.preventDefault();
                await handleCheckCode();
            }

            // Накопление символов (исключаем служебные клавиши)
            if (e.key !== 'Enter' && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
                setCodeInput(prev => prev + e.key);
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [codeInput, handleCheckCode]);

    const handleClearHistory = () => {
        setCheckingHistory([]);
        setExpandedError(null);
        setLastResult(null); // Очищаем последний результат
    };

    // Повторная проверка кода
    const handleRetryCheck = async (code) => {
        setCodeInput(code);
        await handleCheckCode();
    };

    // Переключение развёрнутого состояния ошибки
    const toggleErrorExpansion = (itemId) => {
        setExpandedError(expandedError === itemId ? null : itemId);
    };

    // Получение иконки для типа ошибки
    const getErrorTypeIcon = (errorType, errorCode) => {
        switch (errorType) {
            case 'network':
                return <NetworkIcon color="error" />;
            case 'api':
                if (errorCode === 'UNAUTHORIZED') return <SecurityIcon color="warning" />;
                return <ErrorIcon color="error" />;
            default:
                return <WarningIcon color="error" />;
        }
    };

    // Получение цвета для чипа ошибки
    const getErrorChipColor = (item) => {
        if (item.source === 'local' && item.used) return 'warning';
        if (item.source === 'error') {
            if (item.requiresReauth) return 'warning';
            if (item.isRetryable) return 'info';
            return 'error';
        }
        if (item.source === 'chestnyznak') {
            if (item.valid) {
                if (item.isOwnedByFLX) return 'success'; // Зеленый для FLX
                if (item.isAvailableForSale) return 'primary'; // Синий для доступных но не FLX
                return 'warning'; // Желтый для действительных но недоступных
            }
            if (item.errorCode === '404') return 'error'; // Красный для 404
            return 'default';
        }
        return 'default';
    };

    // Получение текста для чипа
    const getChipLabel = (item) => {
        if (item.source === 'local' && item.used) return `Используется: ${item.message}`;
        if (item.source === 'chestnyznak') {
            if (item.valid) {
                if (item.isOwnedByFLX) return `Действителен`;
                if (item.isAvailableForSale) return `📦 Действителен`;
                return `Недоступен`;
            }
            if (item.errorCode === '404') return `Не найден (404)`;
            return `Недействителен`;
        }
        if (item.source === 'error') {
            if (item.requiresReauth) return `Требуется авторизация`;
            if (item.isRetryable) return `Можно повторить`;
            return `Ошибка: ${item.errorCode || 'Unknown'}`;
        }
        return item.message || 'Unknown';
    };

    const getStatusIcon = (source, valid, used, isAvailableForSale) => {
        if (source === 'error') return <ErrorIcon color="error" />;
        if (used) return <ErrorIcon color="warning" />;
        if (valid) {
            // Предупреждающая иконка для действительных но недоступных кодов
            if (!isAvailableForSale) return <WarningIcon sx={{ color: '#ed6c02' }} />; // оранжевый
            return <CheckIcon color="success" />;
        }
        return <ErrorIcon color="error" />;
    };

  
    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom>
                🔍 Проверка КИ Честный ЗНАК
            </Typography>

            {/* Статус API */}
            {apiStatus && (
                <Alert
                    severity={apiStatus.status === 'available' ? 'success' : 'error'}
                    sx={{ mb: 2 }}
                >
                    API статус: {apiStatus.status === 'available' ? 'Доступен' : 'Недоступен'}
                </Alert>
            )}

            {/* Форма проверки */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Проверить КИ
                    </Typography>

                    {/* Визуальная индикация последнего результата */}
                    {lastResult && (
                        <Box sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 1,
                            bgcolor: lastResult.valid ?
                                    (lastResult.isOwnedByFLX ? '#e8f5e9' :
                                     lastResult.isAvailableForSale ? '#fff8e1' : '#fff8e1') :
                                    (lastResult.source === 'error' || lastResult.errorCode === '404') ? '#ffebee' : 'warning.light', // Мягкий красный для 404
                            border: 2,
                            borderColor: lastResult.valid ?
                                       (lastResult.isOwnedByFLX ? 'success.main' :
                                        lastResult.isAvailableForSale ? '#ffb74d' : '#ffb74d') :
                                       (lastResult.source === 'error' || lastResult.errorCode === '404') ? '#ef5350' : 'warning.main' // Мягкий красный для границы
                        }}>
                            <Typography variant="h6" sx={{
                                color: lastResult.valid ?
                                       (lastResult.isOwnedByFLX ? 'success.dark' :
                                        lastResult.isAvailableForSale ? '#f57f17' : '#f57f17') :
                                       (lastResult.source === 'error' || lastResult.errorCode === '404') ? '#c62828' : 'warning.dark', // Четкий темный красный для текста
                                textAlign: 'center',
                                fontWeight: 'bold'
                            }}>
                                {lastResult.valid ?
                                    (lastResult.isOwnedByFLX ? 'Код действителен и принадлежит FLX' :
                                     lastResult.isAvailableForSale ? '📦 Код действителен' : '⚠️ Код действителен (недоступен)') :
                                    lastResult.source === 'error' ? '❌ Ошибка проверки' : '🚫 Код недействителен'}
                            </Typography>
                            {lastResult.message && !(lastResult.valid && !lastResult.isAvailableForSale) && (
                                <Typography variant="body2" sx={{
                                    color: lastResult.valid ?
                                           (lastResult.isOwnedByFLX ? 'success.main' :
                                            lastResult.isAvailableForSale ? '#ef6c00' : 'warning.main') :
                                           (lastResult.source === 'error' || lastResult.errorCode === '404') ? '#c62828' : 'warning.main', // Оранжевый для второго текста
                                    textAlign: 'center',
                                    mt: 1
                                }}>
                                    {lastResult.message}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            inputRef={inputRef}
                            label="Код маркировки"
                            value={codeInput}
                            onChange={(e) => setCodeInput(e.target.value)}
                            placeholder="Отсканируйте или введите код"
                            disabled={isLoading}
                            sx={{
                                flexGrow: 1,
                                minWidth: 300,
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: lastResult?.valid ?
                                                   (lastResult?.isOwnedByFLX ? 'success.main' :
                                                    lastResult?.isAvailableForSale ? 'primary.main' : 'warning.main') :
                                                   lastResult?.source === 'error' ? 'error.main' : 'warning.main',
                                        borderWidth: 2
                                    }
                                }
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                            onClick={handleCheckCode}
                            disabled={isLoading || !codeInput.trim()}
                            sx={{
                                minWidth: 120,
                                bgcolor: lastResult?.valid ?
                                           (lastResult?.isOwnedByFLX ? 'success.main' :
                                            lastResult?.isAvailableForSale ? 'primary.main' : 'warning.main') :
                                           lastResult?.source === 'error' ? 'error.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: lastResult?.valid ?
                                               (lastResult?.isOwnedByFLX ? 'success.dark' :
                                                lastResult?.isAvailableForSale ? 'primary.dark' : 'warning.dark') :
                                               lastResult?.source === 'error' ? 'error.dark' : 'primary.dark'
                                }
                            }}
                        >
                            {isLoading ? 'Проверка...' : 'Проверить'}
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Typography variant="caption" color="textSecondary">
                        💡 Используйте сканер штрихкодов или введите код вручную.
                    </Typography>
                </CardContent>
            </Card>

            {/* История проверок */}
            {checkingHistory.length > 0 && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                История проверок
                            </Typography>
                            <Button
                                size="small"
                                onClick={handleClearHistory}
                                startIcon={<DeleteIcon />}
                            >
                                Очистить
                            </Button>
                        </Box>

                        <List>
                            {checkingHistory.map((item) => (
                                <Box key={item.id}>
                                    <ListItem divider>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            {item.source === 'error' ?
                                                getErrorTypeIcon(item.errorType, item.errorCode) :
                                                getStatusIcon(item.source, item.valid, item.used, item.isAvailableForSale)
                                            }

                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                    {item.code}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {item.timestamp ?
                                                        (typeof item.timestamp === 'string' ?
                                                            new Date(item.timestamp).toLocaleTimeString() :
                                                            item.timestamp.toLocaleTimeString()
                                                        ) :
                                                        new Date().toLocaleTimeString()
                                                    }
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={getChipLabel(item)}
                                                    color={getErrorChipColor(item)}
                                                    size="small"
                                                    sx={{ maxWidth: 250 }}
                                                />

                                                {/* Кнопки действий для ошибок и детальной информации */}
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {item.isRetryable && (
                                                        <Tooltip title="Повторить проверку">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRetryCheck(item.code)}
                                                                color="primary"
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Показываем кнопку подробностей для всех случаев с детальной информацией */}
                                                    {(item.details || item.cisError || item.cisInfo || (item.details && item.source === 'chestnyznak')) && (
                                                        <Tooltip title={item.valid ? "Подробная информация о коде" : "Подробная информация"}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleErrorExpansion(item.id)}
                                                                color="info"
                                                            >
                                                                {expandedError === item.id ?
                                                                    <ExpandLessIcon fontSize="small" /> :
                                                                    <ExpandMoreIcon fontSize="small" />
                                                                }
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </ListItem>

                                    {/* Развёрнутые детали для всех типов результатов */}
                                    {expandedError === item.id && (
                                        <Collapse in={expandedError === item.id}>
                                            <Box sx={{ pl: 6, pr: 2, pb: 2, bgcolor: 'grey.50' }}>
                                                {/* Информация о коде для валидных результатов */}
                                                {(item.valid || item.cisInfo) && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" color="primary.main" gutterBottom>
                                                            📦 Информация о коде маркировки:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ ml: 2 }}>
                                                            • GTIN: {item.cisInfo?.gtin || 'Не указан'}<br/>
                                                            • Наименование товара: {item.cisInfo?.productName || 'Не указано'}<br/>
                                                            • Бренд: {item.cisInfo?.brand || 'Не указан'}<br/>
                                                            • Владелец: {item.cisInfo?.ownerName || 'Не указан'}<br/>
                                                            • ИНН владельца: {item.cisInfo?.ownerInn || 'Не указан'}
                                                        </Typography>
                                                        {item.cisInfo?.status && (
                                                            <Typography variant="body2" sx={{ ml: 2 }}>
                                                                • Статус: {item.cisInfo.status.russianName || item.cisInfo.status.description}<br/>
                                                                • Описание статуса: {item.cisInfo.status.description}<br/>
                                                                • Доступен для продажи: {item.isAvailableForSale ? 'Да' : 'Нет'}<br/>
                                                                • Принадлежит FLX: {item.isOwnedByFLX ? 'Да ✅' : 'Нет ❌'}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}

                                                {/* Ошибки кода маркировки */}
                                                {item.cisError && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" color="error" gutterBottom>
                                                            ❌ Ошибка кода маркировки:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ ml: 2 }}>
                                                            • Код: {item.cisError.code}<br/>
                                                            • Сообщение: {item.cisError.message}<br/>
                                                            • Описание: {item.cisError.russianDescription}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Информация об ошибках API */}
                                                {item.errorCode && (
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>Код ошибки:</strong> {item.errorCode}
                                                    </Typography>
                                                )}

                                                {item.errorType && (
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>Тип ошибки:</strong> {item.errorType}
                                                    </Typography>
                                                )}

                                                
                                                {/* Время проверки */}
                                                {(item.serverTimestamp || item.timestamp) && (
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        <strong>Время проверки:</strong> {
                                                            item.serverTimestamp ?
                                                                new Date(item.serverTimestamp).toLocaleString() :
                                                                (
                                                                    typeof item.timestamp === 'string' ?
                                                                        new Date(item.timestamp).toLocaleString() :
                                                                        item.timestamp.toLocaleString()
                                                                )
                                                        }
                                                    </Typography>
                                                )}

                                                {/* Предупреждения и рекомендации */}
                                                {item.requiresReauth && (
                                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                                        Требуется повторная авторизация в системе
                                                    </Alert>
                                                )}

                                                {item.isRetryable && (
                                                    <Alert severity="info" sx={{ mt: 1 }}>
                                                        Эту ошибку можно исправить повторной попыткой
                                                    </Alert>
                                                )}

                                                {item.source === 'chestnyznak' && item.errorCode === '404' && (
                                                    <Alert severity="error" sx={{ mt: 1 }}>
                                                        Код маркировки не найден в системе Честный знак, но получена детальная информация по запросу
                                                    </Alert>
                                                )}

                                                {item.valid && (
                                                    <Alert severity={item.isOwnedByFLX ? "success" : item.isAvailableForSale ? "info" : "warning"} sx={{ mt: 1 }}>
                                                        {item.isOwnedByFLX ? 'Код маркировки действителен и принадлежит FLX' :
                                                         item.isAvailableForSale ? '📦 Код маркировки действителен и доступен для продажи' :
                                                         'Код действителен, но недоступен для продажи'}
                                                    </Alert>
                                                )}

                                                {!item.valid && item.isOwnedByFLX && (
                                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                                        Код принадлежит FLX, но недоступен для продажи (неверный статус)
                                                    </Alert>
                                                )}

                                                {!item.valid && !item.isOwnedByFLX && item.cisInfo && item.errorCode !== '404' && (
                                                    <Alert severity="info" sx={{ mt: 1 }}>
                                                        Код не принадлежит FLX и/или недоступен для продажи
                                                    </Alert>
                                                )}
                                            </Box>
                                        </Collapse>
                                    )}
                                </Box>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            )}

            {/* Информация */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon />
                        Управление
                    </Typography>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={checkAPIStatus}
                        sx={{ mt: 1 }}
                    >
                        Обновить статус API
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ChestnyZnakCodeChecker;